import React, { useCallback, useEffect, useState } from "react";
import "./App.css";

import CartItem from "./components/CartItem";
import SuggestionItem from "./components/SuggestionItem";
import StatusBar from "./components/StatusBar";

import {
  PRICE_MAP,
  CATEGORY_KEYWORDS,
  SMART_SUGGESTIONS_MAP,
  SUBSTITUTE_MAP,
  SEASONAL_MAP,
  PRODUCT_CATALOG,
} from "./data/products";

import {
  normalizeSpeechText,
  normalizeHinglishWords,
  extractQuantity,
  cleanItemName,
  isAddIntent,
  isRemoveIntent,
} from "./utils/nlp";

import {
  fetchCartItems,
  addCartItem,
  deleteCartItem,
  clearCart,
  updateQuantity,
  fetchRecommendations,
  clearHistory,
} from "./api/cartApi";

function App() {
  /* ================= STATE ================= */
  const [items, setItems] = useState([]);
  const [historyRecs, setHistoryRecs] = useState([]);

  const [listening, setListening] = useState(false);
  const [spokenText, setSpokenText] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  const [searchResults, setSearchResults] = useState([]);
  const [searchMsg, setSearchMsg] = useState("");

  /* ================= BASIC HELPERS ================= */
  const showStatus = (msg) => {
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(""), 2500);
  };

  const getPrice = (name) => {
    const key = name.toLowerCase();
    return PRICE_MAP[key] ?? 25;
  };

  const guessCategory = (itemName) => {
    const it = itemName.toLowerCase();
    for (const category in CATEGORY_KEYWORDS) {
      if (CATEGORY_KEYWORDS[category].some((x) => it.includes(x))) {
        return category;
      }
    }
    return "Others";
  };

  const cartTotal = items.reduce((sum, item) => sum + getPrice(item.name) * item.quantity, 0);

  // badge should show total quantity
  const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);

  /* ================= API LOADERS ================= */
  const fetchItems = useCallback(async () => {
    try {
      const res = await fetchCartItems();
      setItems(res.data);
    } catch (err) {
      console.error(err);
      showStatus("⚠️ Failed to load cart");
    }
  }, []);

  const loadRecommendations = useCallback(async () => {
    try {
      const res = await fetchRecommendations();
      setHistoryRecs(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    loadRecommendations();
  }, [fetchItems, loadRecommendations]);

  /* ================= VOICE SEARCH HELPERS ================= */
  const extractMaxPrice = (text) => {
    const m = text.match(/\b(under|below|less than)\s*(\d+)/);
    if (m) return parseInt(m[2]);

    const m2 = text.match(/₹\s*(\d+)/);
    if (m2) return parseInt(m2[1]);

    return null;
  };

  const runVoiceSearch = (query) => {
    const q = query.toLowerCase();

    const wantsOrganic = q.includes("organic");
    const wantsPremium = q.includes("premium");
    const wantsSmall = q.includes("small");
    const wantsHealthy = q.includes("healthy");

    const maxPrice = extractMaxPrice(q);

    const knownBrands = [
      "amul",
      "colgate",
      "pepsodent",
      "lays",
      "bingo",
      "maggi",
      "nescafe",
      "dove",
      "dettol",
      "britannia",
    ];
    const brand = knownBrands.find((b) => q.includes(b)) || null;

    let keyword = q
      .replace(/\b(find|search|show|get|me|please|items|item|product|products)\b/g, "")
      .replace(/\b(under|below|less than)\b/g, "")
      .replace(/\d+/g, "")
      .replace(/\borganic\b/g, "")
      .replace(/\bpremium\b/g, "")
      .replace(/\bhealthy\b/g, "")
      .replace(/\bsmall\b/g, "")
      .trim();

    let results = [...PRODUCT_CATALOG];

    // organic fallback message
    let organicApplied = false;
    if (wantsOrganic) {
      const organicResults = results.filter((p) => p.tags.includes("organic"));
      if (organicResults.length > 0) {
        results = organicResults;
        organicApplied = true;
      }
    }

    if (brand) results = results.filter((p) => p.brand.toLowerCase().includes(brand));
    if (maxPrice !== null) results = results.filter((p) => p.price <= maxPrice);

    if (wantsPremium) results = results.filter((p) => p.tags.includes("premium"));
    if (wantsSmall) results = results.filter((p) => p.tags.includes("small"));
    if (wantsHealthy) results = results.filter((p) => p.tags.includes("healthy"));

    if (keyword.length > 0) {
      const key = keyword.trim().toLowerCase();
      const singular = key.endsWith("s") ? key.slice(0, -1) : key;
      const plural = key.endsWith("s") ? key : key + "s";

      results = results.filter((p) => {
        const name = p.name.toLowerCase();
        const cat = p.category.toLowerCase();
        return (
          name.includes(key) ||
          name.includes(singular) ||
          name.includes(plural) ||
          cat.includes(key)
        );
      });
    }

    results = results.slice(0, 10);

    setSearchResults(results);
    setSearchMsg(() => {
      if (results.length === 0) return `❌ No products found for: "${query}"`;
      if (wantsOrganic && !organicApplied)
        return `⚠️ Organic option not available. Showing regular products for: "${query}"`;
      return `🔎 Found ${results.length} results for: "${query}"`;
    });
  };

  const clearSearch = () => {
    setSearchResults([]);
    setSearchMsg("");
    showStatus("🔄 Search cleared");
  };

  /* ================= CART ACTIONS ================= */
  const increaseQty = async (item) => {
    try {
      await updateQuantity(item.id, item.quantity + 1);
      fetchItems();
    } catch (err) {
      console.error(err);
      showStatus("⚠️ Failed to increase quantity");
    }
  };

  const decreaseQty = async (item) => {
    try {
      const newQty = item.quantity - 1;
      await updateQuantity(item.id, newQty);
      fetchItems();
    } catch (err) {
      console.error(err);
      showStatus("⚠️ Failed to decrease quantity");
    }
  };

  const addSuggestedItem = async (suggested) => {
    try {
      await addCartItem({
        name: suggested.name,
        quantity: 1,
        category: suggested.category,
      });

      fetchItems();
      loadRecommendations();
      showStatus(`✅ Added ${suggested.name} (1) to cart`);
    } catch (err) {
      console.error(err);
      showStatus("⚠️ Failed to add item");
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
      fetchItems();
      showStatus("🧹 Cart cleared");
    } catch (err) {
      console.error(err);
      showStatus("⚠️ Failed to clear cart");
    }
  };

  const handleClearHistory = async () => {
    try {
      await clearHistory();
      setHistoryRecs([]);
      showStatus("🧹 History cleared");
    } catch (err) {
      console.error(err);
      showStatus("⚠️ Failed to clear history");
    }
  };

  const findCartItem = (spokenItemName) => {
    const name = spokenItemName.toLowerCase().trim();

    let found = items.find((i) => i.name.toLowerCase() === name);
    if (found) return found;

    found = items.find((i) => i.name.toLowerCase().includes(name));
    if (found) return found;

    const singular = name.endsWith("s") ? name.slice(0, -1) : name;
    return items.find((i) => i.name.toLowerCase().includes(singular));
  };

  /* ================= RECOMMENDATIONS / SUGGESTIONS ================= */
  const getSmartSuggestions = () => {
    const existingItems = items.map((i) => i.name.toLowerCase());
    const suggestionSet = new Set();

    items.forEach((item) => {
      const name = item.name.toLowerCase();
      if (SMART_SUGGESTIONS_MAP[name]) {
        SMART_SUGGESTIONS_MAP[name].forEach((s) => {
          if (!existingItems.includes(s.toLowerCase())) {
            suggestionSet.add(s.toLowerCase());
          }
        });
      }
    });

    return Array.from(suggestionSet).map((name) => ({
      name,
      price: getPrice(name),
      category: guessCategory(name),
    }));
  };

  const getSeasonalSuggestions = () => {
    const month = new Date().getMonth();
    const seasonalItems = SEASONAL_MAP[month] || [];
    const existingItems = items.map((i) => i.name.toLowerCase());

    return seasonalItems
      .filter((name) => !existingItems.includes(name.toLowerCase()))
      .map((name) => ({
        name,
        price: getPrice(name),
        category: guessCategory(name),
      }));
  };

  const getSubstitutes = () => {
    const existingItems = items.map((i) => i.name.toLowerCase());
    const substituteSet = new Set();

    items.forEach((item) => {
      const name = item.name.toLowerCase();
      if (SUBSTITUTE_MAP[name]) {
        SUBSTITUTE_MAP[name].forEach((sub) => {
          if (!existingItems.includes(sub.toLowerCase())) {
            substituteSet.add(sub.toLowerCase());
          }
        });
      }
    });

    return Array.from(substituteSet).map((name) => ({
      name,
      price: getPrice(name),
      category: guessCategory(name),
    }));
  };

  const getRecommendationMessage = (itemName, addCount) => {
    if (addCount >= 8) return `🟡 It looks like you're running low on ${itemName}`;
    if (addCount >= 4) return `⭐ Frequently purchased (${addCount} times)`;
    return `💡 You may need this again (${addCount} times)`;
  };

  const getHistoryBasedRecommendations = () => {
    const existing = items.map((i) => i.name.toLowerCase());

    const normalized = historyRecs.map((h) => {
      const name = (h.itemName || h.item_name || h.name || "").toLowerCase();
      const count = h.addCount ?? h.add_count ?? h.count ?? 0;
      return { itemName: name, addCount: count };
    });

    const sorted = [...normalized].sort((a, b) => b.addCount - a.addCount);

    return sorted
      .filter((h) => h.itemName && !existing.includes(h.itemName))
      .slice(0, 5)
      .map((h) => ({
        name: h.itemName,
        addCount: h.addCount,
        price: getPrice(h.itemName),
        category: guessCategory(h.itemName),
        message: getRecommendationMessage(h.itemName, h.addCount),
      }));
  };

  /* ================= VOICE ================= */
  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech Recognition not supported in this browser");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.start();
    setListening(true);

    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      setSpokenText(text);
      handleCommand(text);
      setListening(false);
    };

    recognition.onerror = () => {
      setListening(false);
      showStatus("⚠️ Voice recognition error");
    };
  };

  const handleCommand = async (text) => {
    try {
      let lower = text.toLowerCase();
      lower = normalizeSpeechText(lower);
      lower = normalizeHinglishWords(lower);

      const quantity = extractQuantity(lower);

      // ✅ search intent
      if (lower.includes("find") || lower.includes("search") || lower.includes("show")) {
        runVoiceSearch(lower);
        showStatus("🔍 Voice search executed");
        return;
      }

      // ✅ add intent
      if (isAddIntent(lower)) {
        const itemName = cleanItemName(lower);
        if (!itemName) return;

        await addCartItem({
          name: itemName,
          quantity,
          category: guessCategory(itemName),
        });

        fetchItems();
        loadRecommendations();
        showStatus(`✅ Added ${itemName} (${quantity}) to cart`);
      }

      // ✅ remove intent
      if (isRemoveIntent(lower)) {
        const itemName = cleanItemName(lower);
        if (!itemName) return;

        const item = findCartItem(itemName);
        if (!item) {
          showStatus(`❌ ${itemName} not found in cart`);
          return;
        }

        let removeQty = quantity;
        if (!removeQty || isNaN(removeQty)) removeQty = 1;

        const newQty = item.quantity - removeQty;

        if (newQty <= 0) {
          await deleteCartItem(item.id);
          showStatus(`🗑️ Removed ${item.name} from cart`);
        } else {
          await updateQuantity(item.id, newQty);
          showStatus(`➖ Removed ${removeQty} ${item.name} from cart`);
        }

        fetchItems();
        loadRecommendations();
      }
    } catch (err) {
      console.error(err);
      showStatus("⚠️ Failed to process voice command");
    }
  };

  /* ================= UI DATA ================= */
  const smartSuggestions = getSmartSuggestions();
  const seasonalSuggestions = getSeasonalSuggestions();
  const substitutes = getSubstitutes();
  const historySuggestions = getHistoryBasedRecommendations();

  /* ================= UI ================= */
 return (
  <div className="container">
    <div className="appCard">
      {/* HEADER */}
      <div className="header">
        <h2 className="title">🛒 Voice Shopping Assistant</h2>
        <div className="badge">{totalQty} items</div>
      </div>

      {/* QUICK COMMANDS */}
      <div className="section quickCommands">
        <div className="sectionTitleRow">
          <h3 className="sectionTitle">✨ Quick Voice Commands</h3>
        </div>
        <p className="muted">
          • add 2 milk <br />
          • remove 1 bread <br />
          • find eggs <br />
          • find organic oranges <br />
          • find toothpaste under 50
        </p>
      </div>

      {/* SPEAK BUTTON */}
      <div className="controls">
        <button className="btn btnPrimary" onClick={startListening}>
          {listening ? "Listening..." : "🎤 Tap to Speak"}
        </button>
      </div>

      {spokenText && <div className="spoken">You said: "{spokenText}"</div>}
      <StatusBar statusMsg={statusMsg} />

      {/* ======================= MAIN ROW ======================= */}
      <div className="mainRow">
        {/* CART BIG */}
        <div className="section cartMain">
          <div className="sectionTitleRow">
            <h3 className="sectionTitle">🛒 Cart</h3>
            <span className="muted">Add / Remove using voice</span>
          </div>

          {items.length === 0 ? (
            <p className="muted">Cart is empty. Try speaking!</p>
          ) : (
            <>
              <div className="cartList">
                {items.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    price={getPrice(item.name)}
                    onIncrease={increaseQty}
                    onDecrease={decreaseQty}
                  />
                ))}
              </div>

              <div className="totalRow">
                <b>Total</b>
                <b>₹{cartTotal}</b>
              </div>

              {/* Clear cart below list */}
              <button className="btn btnDanger" onClick={handleClearCart}>
                Clear Cart
              </button>
            </>
          )}
        </div>

        {/* SEARCH SMALL */}
        <div className="section searchBox">
          <div className="sectionTitleRow">
            <h3 className="sectionTitle">🔍 Searched Items</h3>
            <button className="btn btnMini" onClick={clearSearch}>
              Clear Search
            </button>
          </div>

          <p className="tryHints">
            Try: <b>find eggs,</b> <b>find toothpaste under 50</b>
          </p>

          {searchMsg && <div className="spoken">{searchMsg}</div>}

          <div className="searchResults">
            {searchResults.length === 0 ? (
              <p className="muted">No search results yet.</p>
            ) : (
              searchResults.map((p, idx) => (
                <SuggestionItem
                  key={idx}
                  item={{
                    name: `${p.name} (${p.brand})`,
                    price: p.price,
                    category: p.category,
                  }}
                  onAdd={() =>
                    addSuggestedItem({
                      name: p.name,
                      category: p.category,
                      price: p.price,
                    })
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ======================= ROW 2 ======================= */}
      <div className="gridRow">
        {/* SMART */}
        <div className="section equalCard">
          <div className="sectionTitleRow">
            <h3 className="sectionTitle">💡 Smart Suggestions</h3>
          </div>

          <div className="scrollArea">
            {smartSuggestions.length === 0 ? (
              <p className="muted">No suggestions yet</p>
            ) : (
              smartSuggestions.map((s, idx) => (
                <SuggestionItem key={idx} item={s} onAdd={addSuggestedItem} />
              ))
            )}
          </div>
        </div>

        {/* SEASONAL */}
        <div className="section equalCard">
          <div className="sectionTitleRow">
            <h3 className="sectionTitle">🌿 Seasonal Picks</h3>
          </div>

          <div className="scrollArea">
            {seasonalSuggestions.length === 0 ? (
              <p className="muted">No seasonal picks right now</p>
            ) : (
              seasonalSuggestions.map((s, idx) => (
                <SuggestionItem key={idx} item={s} onAdd={addSuggestedItem} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ======================= ROW 3 ======================= */}
      <div className="gridRow">
        {/* SUBSTITUTES */}
        <div className="section equalCard">
          <div className="sectionTitleRow">
            <h3 className="sectionTitle">🔁 Substitutes</h3>
          </div>

          <div className="scrollArea">
            {substitutes.length === 0 ? (
              <p className="muted">No substitutes available</p>
            ) : (
              substitutes.map((s, idx) => (
                <SuggestionItem key={idx} item={s} onAdd={addSuggestedItem} />
              ))
            )}
          </div>
        </div>

        {/* HISTORY */}
        <div className="section equalCard">
          <div className="sectionTitleRow">
            <h3 className="sectionTitle">🧠 Based on Your History</h3>
            <button className="btn btnMini" onClick={handleClearHistory}>
              Clear History
            </button>
          </div>

          <div className="scrollArea">
            {historySuggestions.length === 0 ? (
              <p className="muted">No history recommendations available</p>
            ) : (
              historySuggestions.map((r, idx) => (
                <div key={idx} style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 900, textTransform: "capitalize" }}>
                    {r.name}
                  </div>
                  <div className="muted" style={{ marginBottom: 8 }}>
                    {r.message}
                  </div>

                  <SuggestionItem
                    item={{ name: r.name, price: r.price, category: r.category }}
                    onAdd={() =>
                      addSuggestedItem({
                        name: r.name,
                        category: r.category,
                        price: r.price,
                      })
                    }
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

}

export default App;
